import type { Department } from './departments';
import { sortDepartmentsByLofThenName } from './departmentUi';

export type CrossAuditDepartmentOption = { id: string; name: string };

/** Audited (home) wards where HEAD_WARD_NURSE gets an ECC-only cross-audit auditor dropdown. */
export const HEAD_WARD_NURSE_ECC_ONLY_CROSS_AUDIT_HOME_DEPARTMENT_IDS = ['BLO_BNK'] as const;

/** Home wards where HEAD_WARD_NURSE cross-audit "ตรวจไขว้โดย" omits med_error_incident-only OPD_Primary / OPD_PC. */
export const HEAD_WARD_NURSE_CROSS_AUDIT_HIDE_OPD_PRIMARY_PC_FOR_HOME_DEPARTMENT_IDS = ['ECC'] as const;

/** Allowed cross-audit auditors when HOME_DEPARTMENT_IDS + HEAD_WARD_NURSE rule applies (ECC / ศูนย์ส่งเสริมฟื้นฟูสุขภาพผู้สูงอายุ). */
export const HEAD_WARD_NURSE_CROSS_AUDIT_AUDITOR_DEPARTMENT_IDS = ['ECC'] as const;

const resolveDepartmentLofId = (dept: Department) => {
  const raw = ((dept.lofId ?? (dept as { lof?: string }).lof ?? (dept as { lofRef?: any }).lofRef?.id ?? (dept as { LOF?: any }).LOF?.id ?? '') as string).trim();
  return raw;
};

export function getCrossAuditDepartmentOptions(args: {
  departments: Department[];
  auditorDepartmentId: string | null;
  currentLofId: string | null;
  scopedDepartments?: Department[];
  /**
   * When set, only these departments appear as cross-audit auditors.
   * Ignores LOF filtering so ECC can appear when the home ward is under another สายงาน.
   */
  restrictAuditorPickerToDepartmentIds?: readonly string[] | null;
}): CrossAuditDepartmentOption[] {
  const { departments, auditorDepartmentId, currentLofId, scopedDepartments, restrictAuditorPickerToDepartmentIds } =
    args;
  const resolvedLofId = currentLofId?.trim() || null;

  const mapDepartments = (list: Department[]) =>
    sortDepartmentsByLofThenName(list.filter((dept) => dept.id !== auditorDepartmentId)).map(
      (dept) => ({ id: dept.id, name: dept.name ?? dept.id }),
    );

  if (restrictAuditorPickerToDepartmentIds?.length) {
    const allow = new Set(restrictAuditorPickerToDepartmentIds.map((id) => id.trim()).filter(Boolean));
    return mapDepartments(departments.filter((dept) => allow.has(dept.id)));
  }

  const eligibleDepartments = resolvedLofId
    ? departments.filter((dept) => resolveDepartmentLofId(dept) === resolvedLofId)
    : departments;

  if (!resolvedLofId) return [];
  if (eligibleDepartments.length) return mapDepartments(eligibleDepartments);

  const fallbackBase = scopedDepartments?.length ? scopedDepartments : departments;
  const fallback = fallbackBase.filter((dept) => resolveDepartmentLofId(dept) === resolvedLofId);
  return mapDepartments(fallback);
}
